import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Tooltip,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-material-ui'
import * as Yup from 'yup'
import DateField from '../../../components/DateField/DateField'
import AutoComplete from '../../../components/AutoComplete/AutoComplete'
import { numbersOnly, validarEmail } from '../../../utils/validacoes'
import { useContext, useRef } from 'react'
import { metodoPrograma } from '../../../components/api'
import CurrencyField from '../../../components/CurrencyField/CurrencyField'
import { cepMask, cpfMask, telefoneMask } from '../../../utils/masks'
import dayjs from 'dayjs'
import { handleVlTotal, PropostaContext } from '.'
import { setSuccess } from '../../../components/alerts'
import { BsFiletypePdf, BsTrash } from 'react-icons/bs'
import ModalPdf from './ModalPdf'
import { useModal } from '../../../components/Utils/UseModal'
import { getViaCep } from '../../../utils/api/viacep'

const validationSchema = Yup.object().shape({
  dsNome: Yup.string().required('Campo Obrigatório!'),
  dtCheckin: Yup.date().nullable().required('Campo Obrigatório!'),
  dtCheckout: Yup.date()
    .nullable()
    .required('Campo Obrigatório!')
    .min(
      Yup.ref('dtCheckin'),
      'A data de Check Out não pode ser anterior à data de Check In',
    ),
  dtPagamentoCaucao: Yup.date().nullable(),
  dsEmail: Yup.string()
    .test(
      'is-email',
      () => `Insira um Email Valido`,
      (value) => value == null || validarEmail(value),
    )
    .optional()
    .nullable(),
  snEstrangeiro: Yup.boolean(),
  cdDocumentoEstrangeiro: Yup.string().when('snEstrangeiro', {
    is: true,
    then: () =>
      Yup.string()
        .required('Campo Obrigatório!')
        .min(2, 'Documento deve ter pelo menos 2 caracteres'),
    otherwise: () => Yup.string().optional(),
  }),
  cdCpf: Yup.string().when('snEstrangeiro', {
    is: true,
    then: () => Yup.string().optional(),
    otherwise: () =>
      Yup.string()
        .min(11, 'CPF deve ter pelo menos 11 caracteres')
        .required('Campo Obrigatório!'),
  }),

  tpSituacao: Yup.string().required('Campo Obrigatório!'),
  dsSaidaAeroporto: Yup.string().optional().nullable(),
  dsSaidaH: Yup.string().optional().nullable(),
  dsEndereco: Yup.string().optional().nullable(),
})

const DadosProposta = () => {
  const { dadosEdicao, handleSalvar, dispatch, formatCPF } =
    useContext(PropostaContext)
  const formikRef = useRef()

  const modalPdf = useModal(ModalPdf)

  const setVlCalcao = (value) =>
    dispatch({
      type: 'updateCaucao',
      value,
      valuesOld: formikRef?.current?.values,
    })

  // ─── CORREÇÃO PRINCIPAL ──────────────────────────────────────────────────────
  // 1. Aceita flag `isEstrangeiro` para não aplicar numbersOnly no doc estrangeiro
  // 2. Removido o dispatch de dentro desta função — ele causava re-render via
  //    enableReinitialize, que resetava o Formik antes dos setFieldValue persistirem
  // 3. setFieldValue é chamado via formikRef.current capturado APÓS a promise
  // ────────────────────────────────────────────────────────────────────────────
  const buscaPacienteDoc = async (cdDoc, isEstrangeiro = false) => {
    const formik = formikRef?.current
    if (!formik) return

    const cdDocFormatado = isEstrangeiro ? cdDoc?.trim() : numbersOnly(cdDoc)

    if (!cdDocFormatado) return

    const {
      status,
      nmPaciente,
      dtNascimento,
      dsEmail,
      nrTelefone,
      idPaciente,
      cdCpf,
      dsLogradouro,
      nrLogradouro,
      dsBairro,
      dsComplemento,
      cdCep,
      acPais,
      acCidade,
      acEstado,
      acForma,
    } = await metodoPrograma('buscaPacientePeloDoc', { cdDoc: cdDocFormatado })

    // Re-captura a ref após await, pois pode ter mudado
    const { setFieldValue } = formikRef.current

    if (status) {
      if (cdCpf) setFieldValue('cdCpf', cdCpf)
      if (nmPaciente) setFieldValue('dsNome', nmPaciente ?? '')
      if (dtNascimento)
        setFieldValue(
          'dtNascimento',
          dtNascimento ? dayjs(dtNascimento, 'YYYY-MM-DD') : null,
        )
      if (dsEmail) setFieldValue('dsEmail', dsEmail ?? '')
      if (nrTelefone) setFieldValue('nrTelefone', nrTelefone ?? '')
      if (idPaciente) setFieldValue('idPaciente', idPaciente ?? null)
      if (dsLogradouro) setFieldValue('dsLogradouro', dsLogradouro ?? '')
      if (nrLogradouro) setFieldValue('nrLogradouro', nrLogradouro ?? '')
      if (dsBairro) setFieldValue('dsBairro', dsBairro ?? '')
      if (dsComplemento) setFieldValue('dsComplemento', dsComplemento ?? '')
      if (cdCep) setFieldValue('cdCep', cdCep ?? '')
      if (acPais) setFieldValue('acPais', acPais ?? null)
      if (acCidade) setFieldValue('acCidade', acCidade ?? null)
      if (acEstado) setFieldValue('acEstado', acEstado ?? null)
      if (acForma) setFieldValue('acForma', acForma ?? null)
    }
  }

  const fecharProposta = (idAtendimento) => () => {
    metodoPrograma('fecharProposta', {
      idAtendimento,
    }).then((res) => {
      if (res.status) {
        setSuccess('Proposta finalizada com sucesso!')
        dispatch({ type: 'setTela', value: 'lista' })
        dispatch({ type: 'setDados', value: null })
      }
    })
  }

  const cancelarProposta = (idAtendimento) => () => {
    metodoPrograma('cancelarProposta', {
      idAtendimento,
    }).then((res) => {
      if (res.status) {
        setSuccess('Proposta cancelada com sucesso!')
        dispatch({ type: 'setTela', value: 'lista' })
        dispatch({ type: 'setDados', value: null })
      }
    })
  }

  const openModalPdf = async () => {
    const values = formikRef.current?.values || dadosEdicao
    let terapiasFormatadas = []
    let pacotesFormatados = []
    let pacientesFormatados = []

    if (values.idAtendimento) {
      const resTerapias = await metodoPrograma('listaTerapia', {
        idAtendimento: values.idAtendimento,
      })
      if (resTerapias?.lista) {
        terapiasFormatadas = resTerapias.lista.map((item) => ({
          nome: item?.[2]?.[1],
          valor: handleVlTotal(item?.[4]),
        }))
      }

      const resItens = await metodoPrograma('listaTipoAtendimento', {
        idAtendimento: values.idAtendimento,
      })
      if (resItens?.lista) {
        pacotesFormatados = resItens.lista.map((item) => ({
          nome: item?.[2]?.[1] || 'NÃO CONSEGUI',
          pacientes: (item?.[5] || [])
            .map((paciente) => paciente?.[1])
            .filter(Boolean),
          valor: handleVlTotal(item?.[4]),
        }))
      }
    }

    const resPacientes = await metodoPrograma('listaPaciente', {
      idAtendimento: values.idAtendimento,
    })
    if (resPacientes?.lista) {
      pacientesFormatados = resPacientes.lista.map((item) => ({
        nome: item[1],
        cpf: item[2] ? formatCPF(item[2]) : null,
      }))
    }

    console.log('obs', JSON.stringify(values.dsObservacoes))

    const payload = {
      proposta: values.idAtendimento ? String(values.idAtendimento) : '',
      observacoes: values.dsObservacoes || '',
      contratante: values.dsNome || '',
      suite: values.acQuarto?.[1] || 'NÃO CONSEGUI',
      checkIn: values.dtCheckin
        ? dayjs(values.dtCheckin).format('DD/MM/YYYY')
        : '',
      checkOut: values.dtCheckout
        ? dayjs(values.dtCheckout).format('DD/MM/YYYY')
        : '',
      pessoas: pacientesFormatados,
      pacotes: pacotesFormatados,
      terapias: terapiasFormatadas,
      subTotal: handleVlTotal(values.vlSubTotal),
      caucao: handleVlTotal(values.vlCaucao),
      total: handleVlTotal(values.vlSubTotal, values.vlCaucao),
    }

    await modalPdf(payload)
  }

  const handleViaCep = async (e) => {
    const ref = formikRef.current

    ref.setFieldValue('cdCep', e?.target?.value)

    if (ref?.values?.acPais?.[0]?.[0] !== 'BR') return

    if (numbersOnly(e.target.value)?.length < 7) return
    if (ref?.values?.cdCep !== e?.target?.value) {
      const resp = await getViaCep(e?.target?.value)

      if (!resp?.ibge) return

      ref.setFieldValue('acEstado', [[resp?.uf], resp?.uf])
      ref.setFieldValue('acCidade', [[Number(resp?.ibge)], resp?.localidade])
      ref.setFieldValue('dsLogradouro', resp?.logradouro)
      ref.setFieldValue('dsBairro', resp?.bairro)
    }
  }

  return (
    <Formik
      initialValues={dadosEdicao}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      innerRef={formikRef}
      onSubmit={async (values) => {
        const payload = {
          ...values,
          ufCidade: values.acEstado?.[0]?.[0],
          idCidade: values.acCidade?.[0]?.[0],
          idForma: values.acForma?.[0]?.[0],
          cdPais: values.acPais?.[0]?.[0] || 'BR',
          cdCep:
            values.acPais?.[0]?.[0] === 'BR' && values.cdCep
              ? numbersOnly(values.cdCep)
              : values.cdCep || undefined,
          snEstrangeiro: values.snEstrangeiro ? 'S' : 'N',
          cdDocumentoEstrangeiro: values.cdDocumentoEstrangeiro || '',
        }
        // dispatch só acontece aqui, no submit — não mais dentro de buscaPacienteDoc
        dispatch({
          type: 'dadosEdicao',
          value: { ...payload, snEstrangeiro: values.snEstrangeiro },
        })
        return await handleSalvar(payload)
      }}
    >
      {({
        submitForm,
        values,
        setFieldValue,
        setFieldTouched,
        validateForm,
        errors,
        touched,
        handleBlur,
      }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid container spacing={2} style={{ padding: 20 }} item xs={12}>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.snEstrangeiro || false}
                      onChange={(_, checked) => {
                        setFieldValue('snEstrangeiro', checked)
                        if (checked) {
                          setFieldValue('cdCpf', '')
                        }
                        setTimeout(() => {
                          validateForm().then(() => {
                            setFieldTouched(
                              checked ? 'cdDocumentoEstrangeiro' : 'cdCpf',
                              true,
                            )
                          })
                        }, 0)
                      }}
                    />
                  }
                  label="Estrangeiro"
                  labelPlacement="start"
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  sx={{ mt: 1, ml: 0 }}
                />
              </Grid>

              {values.snEstrangeiro ? (
                <Grid item xs={12} md={3}>
                  <Field
                    component={TextField}
                    disabled={values.idAtendimento && values.tpSituacao !== '0'}
                    name="cdDocumentoEstrangeiro"
                    InputLabelProps={{ shrink: true }}
                    label="Documento Estrangeiro"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    onBlur={async (e) => {
                      // ─── CORREÇÃO: passa isEstrangeiro=true, sem numbersOnly ───
                      const docValue = e.target.value
                      setFieldValue('cdDocumentoEstrangeiro', docValue)
                      await buscaPacienteDoc(docValue, true)
                      handleBlur(e)
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={3}>
                  <Field
                    component={TextField}
                    disabled={values.idAtendimento && values.tpSituacao !== '0'}
                    name="cdCpf"
                    InputLabelProps={{ shrink: true }}
                    label="CPF"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    size="small"
                    InputProps={cpfMask}
                    onChange={(e) => {
                      setFieldValue('cdCpf', e.target.value)
                      const numeroCpf = numbersOnly(e.target.value)
                      if (numeroCpf.length === 11)
                        buscaPacienteDoc(e.target.value)
                    }}
                    onBlur={(e) => {
                      handleBlur(e)
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={7}>
                <Field
                  component={TextField}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  name="dsNome"
                  label="Nome"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                  onBlur={(e) => {
                    handleBlur(e)
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={DateField}
                  name="dtNascimento"
                  label="Dt. Nascimento"
                  fullWidth
                  value={values?.dtNascimento}
                  onChange={(v) => {
                    setFieldValue('dtNascimento', v)
                  }}
                  slotProps={{
                    textField: {
                      helperText: touched?.dtNascimento && errors?.dtNascimento,
                      error: touched?.dtNascimento && !!errors?.dtNascimento,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  component={TextField}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  name="dsEmail"
                  InputLabelProps={{ shrink: true }}
                  label="E-mail"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="nrTelefone"
                  label="Telefone"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                  InputProps={telefoneMask}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <AutoComplete
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  label="País"
                  metodo="buscaPais"
                  value={values.acPais || [['BR'], 'Brasil']}
                  size="small"
                  onChange={(_, val) => {
                    setFieldValue('acPais', val || [['BR'], 'Brasil'])
                    setFieldValue('acEstado', null)
                    setFieldValue('acCidade', null)
                  }}
                  textFieldProps={{
                    helperText: touched?.acPais && errors?.acPais,
                    error: touched?.acPais && !!errors?.acPais,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  component={TextField}
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  name="cdCep"
                  label={
                    values.acPais?.[0]?.[0] === 'BR' ? 'CEP' : 'Código Postal'
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  onChange={
                    values.acPais?.[0]?.[0] === 'BR' ? handleViaCep : undefined
                  }
                  InputProps={
                    values.acPais?.[0]?.[0] === 'BR' ? cepMask : undefined
                  }
                  inputProps={
                    values.acPais?.[0]?.[0] === 'BR'
                      ? undefined
                      : { maxLength: 8 }
                  }
                  size="small"
                  helperText={errors?.cdCep}
                  error={!!errors?.cdCep}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <AutoComplete
                  InputLabelProps={{ shrink: true }}
                  disabled={
                    (values.idAtendimento && values.tpSituacao !== '0') ||
                    !values.acPais?.[0]?.[0]
                  }
                  label="Estado"
                  metodo="buscaEstado"
                  value={values.acEstado || null}
                  size="small"
                  searchParams={{ cdPais: values.acPais?.[0]?.[0] }}
                  onChange={(_, val) => {
                    setFieldValue('acEstado', val)
                    setFieldValue('acCidade', null)
                  }}
                  textFieldProps={{
                    helperText: touched?.acEstado && errors?.acEstado,
                    error: touched?.acEstado && !!errors?.acEstado,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <AutoComplete
                  InputLabelProps={{ shrink: true }}
                  disabled={
                    (values.idAtendimento && values.tpSituacao !== '0') ||
                    values.acEstado?.[0] === undefined
                  }
                  label="Cidade"
                  metodo="buscaCidade"
                  value={values.acCidade}
                  size="small"
                  searchParams={{
                    cdPais: values.acPais?.[0]?.[0],
                    ufCidade: values.acEstado?.[0]?.[0],
                  }}
                  onChange={(_, val) => setFieldValue('acCidade', val)}
                  textFieldProps={{
                    helperText: touched?.acCidade && errors?.acCidade,
                    error: touched?.acCidade && !!errors?.acCidade,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsLogradouro"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Logradouro"
                  helperText={errors?.dsLogradouro}
                  error={!!errors?.dsLogradouro}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="nrLogradouro"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Número"
                  helperText={errors?.nrLogradouro}
                  error={!!errors?.nrLogradouro}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsComplemento"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Complemento"
                  helperText={errors?.dsComplemento}
                  error={!!errors?.dsComplemento}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsBairro"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Bairro"
                  helperText={errors?.dsBairro}
                  error={!!errors?.dsBairro}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <AutoComplete
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  label="Forma Contato"
                  metodo="buscaForma"
                  value={values.acForma || null}
                  size="small"
                  onChange={(_, val) => setFieldValue('acForma', val)}
                  textFieldProps={{
                    helperText: touched?.acForma && errors?.acForma,
                    error: touched?.acForma && !!errors?.acForma,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsSaidaAeroporto"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Saída do Aeroporto"
                  helperText={errors?.dsSaidaAeroporto}
                  error={!!errors?.dsSaidaAeroporto}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsSaidaH"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Saída H"
                  helperText={errors?.dsSaidaH}
                  error={!!errors?.dsSaidaH}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsEndereco"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  label="Endereço Embarque"
                  helperText={errors?.dsEndereco}
                  error={!!errors?.dsEndereco}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  component={AutoComplete}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  metodo="buscaQuarto"
                  codigo="CtrlProposta"
                  value={values.acQuarto || null}
                  onChange={(_, val) => setFieldValue('acQuarto', val)}
                  name="acQuarto"
                  label="Quarto"
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  textFieldProps={{
                    helperText: touched?.acQuarto && errors?.acQuarto,
                    error: touched?.acQuarto && !!errors?.acQuarto,
                  }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={DateField}
                  name="dtCheckin"
                  label="Check In"
                  fullWidth
                  value={values?.dtCheckin}
                  slotProps={{
                    textField: {
                      helperText: touched?.dtCheckin && errors?.dtCheckin,
                      error: touched?.dtCheckin && !!errors?.dtCheckin,
                    },
                  }}
                  onChange={(v) => setFieldValue('dtCheckin', v)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={DateField}
                  name="dtCheckout"
                  label="Check Out"
                  fullWidth
                  value={values?.dtCheckout}
                  minDate={values?.dtCheckin || undefined}
                  slotProps={{
                    textField: {
                      helperText: touched?.dtCheckout && errors?.dtCheckout,
                      error: touched?.dtCheckout && !!errors?.dtCheckout,
                    },
                  }}
                  onChange={(v) => setFieldValue('dtCheckout', v)}
                  margin="dense"
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled
                  component={TextField}
                  name="tpSituacao"
                  select
                  variant="outlined"
                  margin="dense"
                  label="Situação"
                  fullWidth
                  size="small"
                >
                  <MenuItem selected value="">
                    Selecione
                  </MenuItem>
                  <MenuItem value="0">Negociação Iniciada</MenuItem>
                  <MenuItem value="1">Negociação Finalizada</MenuItem>
                  <MenuItem value="2">Negociação Canelada</MenuItem>
                  <MenuItem value="3">Em Atendimento</MenuItem>
                  <MenuItem value="9">Atendimento Finalizado</MenuItem>
                </Field>
              </Grid>

              <Grid item xs={12} md={12}>
                <Field
                  InputLabelProps={{ shrink: true }}
                  disabled={values.idAtendimento && values.tpSituacao !== '0'}
                  component={TextField}
                  name="dsObservacoes"
                  label="Observações"
                  fullWidth
                  multiline
                  rows={2}
                  margin="dense"
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid
                justifyContent="flex-end"
                container
                spacing={2}
                item
                xs={12}
              >
                <Grid item xs={12} md={2}>
                  <Field
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    label="Caução"
                    size="small"
                    disabled
                    value={values.vlCaucao}
                    component={CurrencyField}
                    name="vlCaucao"
                    onChange={(v) => {
                      setFieldValue('vlCaucao', v)
                      setVlCalcao && setVlCalcao(v)
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Field
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="vlSubTotal"
                    disabled
                    label="Subtotal"
                    size="small"
                    value={handleVlTotal(values.vlSubTotal)}
                    component={TextField}
                  />
                </Grid>

                {Number(values.vlDescontoCheckin || 0) > 0 && (
                  <Grid item xs={12} md={2}>
                    <Field
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      name="vlDescontoCheckin"
                      disabled
                      label="Desconto Check-in"
                      size="small"
                      value={values.vlDescontoCheckin}
                      component={CurrencyField}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={2}>
                  <Field
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="vlTotal"
                    disabled
                    label="Saldo Devedor"
                    size="small"
                    value={handleVlTotal(
                      values?.vlSubTotal,
                      values?.vlCaucao,
                      values?.vlDescontoCheckin,
                    )}
                    component={TextField}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            style={{ padding: 20 }}
            xs={12}
            spacing={2}
            container
            justifyContent="flex-end"
          >
            <Grid item xs={12} md={2} style={{ textAlign: 'end' }}>
              <Tooltip title="Gerar PDF">
                <span>
                  <IconButton
                    disabled={!values.idAtendimento}
                    onClick={openModalPdf}
                  >
                    <BsFiletypePdf />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cancelar Proposta">
                <span>
                  <IconButton
                    disabled={
                      !values.idAtendimento || values.tpSituacao !== '0'
                    }
                    onClick={cancelarProposta(values.idAtendimento)}
                  >
                    <BsTrash />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={4}>
              <Button
                onClick={fecharProposta(values.idAtendimento)}
                variant="contained"
                disabled={!values.idAtendimento || values.tpSituacao !== '0'}
                fullWidth
                color="error"
                style={{ margin: '5px' }}
              >
                Fechar proposta
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                onClick={submitForm}
                variant="contained"
                disabled={values.idAtendimento && values.tpSituacao !== '0'}
                fullWidth
                color="primary"
                style={{ margin: '5px' }}
              >
                Salvar
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default DadosProposta
